import * as React from 'react';
import { useNavigate, useParams } from "react-router-dom";
import configData from "../config.json";
import { GetSession, GetToken } from '../components/Session';
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { FormInput, FormSwitch, FormTextArea, FormNumericTextBox } from "../components/formComponents";
import { Button } from "@progress/kendo-react-buttons";
import { GetPermissions } from '../components/Options';

const EditVehicleConfigurations = () => {
    const { id } = useParams(); // offences/id url is used to edit existing offense, otherwise id is undefined
    const saveName = id === undefined ? "Create" : "Update";
    const [formkey, setFormkey] = React.useState(0); // seems to be needed to push values to form after loading
    const [permissions, setPermissions] = React.useState(null);
    const navigate = useNavigate();
    if (localStorage.getItem("userid") === null) navigate("/logout");

    // default layout data for page
    const [layout, setLayout] = React.useState([
        {"field": "name","label": "Name","defaultValue": "","isVisible": true,"type": "text"},
        {"field": "code","label": "Code","defaultValue": "","isVisible": true,"type": "text"},
        {"field": "description","label": "Comment","defaultValue": "","isVisible": true,"type": "text"},
        {"field": "maxLoad","label": "Max GVM (t)","defaultValue": 0, "isVisible": true,"type": "numeric"},
        {"field": "noOfAxleGroups","label": "No of Axle Groups","defaultValue": 0,"isVisible": true,"type": "numeric"},
        {"field": "tare","label": "Tare (t)","defaultValue": 0,"isVisible": false,"type": "numeric"},
        {"field": "active","label": "Active","defaultValue": "true","isVisible": true,"type": "boolean"}
    ]);

    // Gets field from layout based on field name
    const GetField = (f) => {
        return layout.find(obj => { return obj.field === f });
    }

    // State data for form. Values match form value on submit
    const [values, setValues] = React.useState({
        name: GetField("name")["defaultValue"],
        code: GetField("code")["defaultValue"],
        description: GetField("description")["defaultValue"],
        maxLoad: GetField("maxLoad")["defaultValue"],
        noOfAxleGroups: GetField("noOfAxleGroups")["defaultValue"],
        tare: GetField("tare")["defaultValue"],
        active: String(GetField("active")["defaultValue"]).toLowerCase() === "true"
      });

    // When page first loads:
    // - populate drop downs from apiv7/options
    // - if in edit mode, set initial values for all fields for apiv7/vehicleconfigurations
    React.useEffect(() => {
        GetPermissions(["ViewTruckConfiguration", "NewTruckConfiguration", "EditTruckConfiguration"], setPermissions);
        var url = configData.SERVER_URL + "apiv7/userinterfaces?$filter=viewName eq 'EditVehicleConfigurations'";
        const init = {
          method: 'GET',
          accept: 'application/json',
          headers: {"Authorization": "Bearer " + GetToken()}
        }
        fetch(url, init).then(response => response.json()).then(d => { 
            if (GetSession().IsMobile){
                let data = [];
                for(let i = 0; i < d.length; i++){
                    data.push({field: d[i].field, label: d[i].label, type:d[i].type, format: d[i].format, isVisible: d[i].isVisible && d[i].isVisibleOnMobile});
                }
                setLayout(data);
            } else {
                setLayout(d); 
            }
        });        url = configData.SERVER_URL + "apiv7/options";
        fetch(url, init).then(response => response.json()).then(opt => {           
            if (id !== undefined){
                url = `${configData.SERVER_URL}apiv7/vehicleconfigurations/${id}`;
                fetch(url, init).then(response => response.json()).then(d => {
                    setValues({
                        name: d.name === null ? "" : d.name,
                        code: d.code === null ? "" : d.code,
                        description: d.description === null ? "" : d.description,
                        maxLoad: d.maxLoad === null ? 0 : d.maxLoad,
                        tare: d.tare === null ? 0 : d.tare,
                        noOfAxleGroups: d.noOfAxleGroups === null ? 0 : d.noOfAxleGroups,
                        active: d.isActive === null ? true : d.isActive
                    });
                    setFormkey(formkey + 1);
                })
            }
        });
    }, []);

    // Returns value to be sent to web api:
    // If field is visible just return f
    // If field is hidden, return defaultValue from layout data
    const GetDefaultData = (f, name) => {
        let fld = GetField(name);
        if (fld === undefined || fld === null) return f;
        if (fld["isVisible"]) return f;
        let res = "";
        switch(fld["type"]){
            case "text": res = fld["defaultValue"]; break;
            case "numeric": res = parseFloat(fld["defaultValue"]); break;
            case "boolean": res = fld["defaultValue"] === "true"; break;
            default: res = fld["defaultValue"]; break;
        }
        return res;
    }

    const isNullOrEmpty = (x) => { return (x === undefined || x === null || x === ""); }

    const Validate = (dataItem) => {
        let st = "";
        if (isNullOrEmpty(dataItem.name)) st += "Name\r\n";
        if (isNullOrEmpty(dataItem.code)) st += "Code\r\n";
        if (st !== "") {
            alert("Please enter the following: \r\n" + st);
            return false;
        }
        return true;
    }

    // Handle submit button. For create POST the data, for edit PUT the data, then redirect to offenses index page.
    const handleSubmit = () => {
        let dataItem = { ...values };
        if (!Validate(dataItem)) return;  
        var pth = id === undefined ? "" : "/" + id;
        var url = `${configData.SERVER_URL}apiv7/vehicleconfigurations${pth}`;
        const init = {
          method: id === undefined ? 'POST' : 'PUT',
          accept: "application/json",
          headers: {
            "Content-type": "application/json",
            "Authorization": "Bearer " + GetToken()
          },
          mode: "cors",
          cache: "no-cache",
          body: JSON.stringify({
            "vehicleConfigurationId": id === undefined ? 0 : id,
            "name": GetDefaultData(dataItem.name, "name"),
            "code": GetDefaultData(dataItem.code, "code"),
            "description":  GetDefaultData(dataItem.description, "description"),           
            "maxLoad": GetDefaultData(dataItem.maxLoad, "maxLoad"),
            "tare": GetDefaultData(dataItem.tare, "tare"),
            "noOfAxleGroups": GetDefaultData(dataItem.noOfAxleGroups, "noOfAxleGroups"),
            "isActive": GetDefaultData(dataItem.active, "active")
            })
        }
        fetch(url, init).then(response => response).then(d => {           
            var msg = id === undefined ? "Truck Configuration created" : "Truck Configuration updated";
            Redirect(msg);
        });
    }

    // navigation
    const GoBack = () => Redirect("");
    const Redirect = (msg = "") => {
       navigate("/vehicleconfigurations", {state: {msg: msg}});
    }
    if (permissions && !permissions.CanView) navigate("/error401");
    if (permissions && !permissions.CanEdit && id !== undefined) navigate("/error401");
    if (permissions && !permissions.CanCreate && id === undefined) navigate("/error401");

    const NameChanged = (e) => { let v = { ...values }; v.name = e.value; setValues(v) }
    const CodeChanged = (e) => { let v = { ...values }; v.code = e.value; setValues(v) }
    const GroupsChanged = (e) => { let v = { ...values }; v.noOfAxleGroups = e.value; setValues(v) }
    const MaxLoadChanged = (e) => { let v = { ...values }; v.maxLoad = e.value; setValues(v) }
    const TareChanged = (e) => { let v = { ...values }; v.tare = e.value; setValues(v) }
    const DescriptionChanged = (e) => { let v = { ...values }; v.description = e.value; setValues(v) }
    const ActiveChanged = (e) => { let v = { ...values }; v.active = e.value; setValues(v) }

    // form validators
    const textValidator = (value) =>  !value ? "Please enter a value" : "";
    return (
        <div style={{padding: 20}}>
            <Form initialValues={values} key={formkey} render={(formRenderProps) => (
                <FormElement style={{width: "98%"}} >
                    <fieldset className={"k-form-fieldset"}>
                        <div className="row">
                           {GetField("name")["isVisible"] && <div className="col-md-4">
                                <Field id={"name"} name={"name"} label={GetField("name")["label"]} component={FormInput} validator={textValidator} onChange={NameChanged} />
                            </div>}
                            {GetField("code")["isVisible"] && <div className="col-md-4">
                                <Field id={"code"} name={"code"} label={GetField("code")["label"]} component={FormInput} validator={textValidator} onChange={CodeChanged} />
                            </div>}
                        </div>
                        <div className="row">
                           {GetField("noOfAxleGroups")["isVisible"] && <div className="col-md-4">
                                <Field id={"noOfAxleGroups"} name={"noOfAxleGroups"} label={GetField("noOfAxleGroups")["label"]} component={FormNumericTextBox} min={2} max={10} onChange={GroupsChanged} />
                            </div>}
                            {GetField("maxLoad")["isVisible"] && <div className="col-md-4">
                                <Field id={"maxLoad"} name={"maxLoad"} label={GetField("maxLoad")["label"]} component={FormNumericTextBox} min={0} max={1000} onChange={MaxLoadChanged} />
                            </div>}
                        </div>
                        <div className="row">
                           {GetField("tare")["isVisible"] && <div className="col-md-4">
                                <Field id={"tare"} name={"tare"} label={GetField("tare")["label"]} component={FormNumericTextBox} min={0} max={1000} onChange={TareChanged} />
                            </div>}
                        </div>
                        <div className='row' >
                            {GetField("description")["isVisible"] && <div id="description" className='col-md-8'>
                                <Field id={"description"} name={"description"} label={GetField("description")["label"]} component={FormTextArea} onChange={DescriptionChanged} />
                            </div>}
                        </div>
                        <div className='row'>
                            {GetField("active")["isVisible"] && <div className='col-md-4'>
                                <Field id={"active"} name={"active"} label={GetField("active")["label"]} component={FormSwitch} onChange={ActiveChanged} />
                            </div>}
                        </div>
                    </fieldset>
                    <div className="k-form-buttons" style={{paddingTop: 10}}>
                        <div className='col-md-1'>
                            <Button themeColor={"primary"} className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base" onClick={handleSubmit}>{saveName}</Button>
                        </div>
                        <div className='col-md-1'>
                            <Button className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base" onClick={GoBack}>Back to List</Button>
                        </div>
                    </div>                
                </FormElement>
            )} />
        </div>
    );
}

export default EditVehicleConfigurations